import fetch from 'isomorphic-fetch';
import throttledQueue from 'throttled-queue';

import { BATCH_SIZE, THROTTLE_AMOUNT, THROTTLE_TIME, TRANSLATION_TAG_PREFIX } from './constants';

export enum Status {
  Success,
  Failure,
}

export enum SuccessTypes {
  AllTranslated = 'Congrats! It looks like all your text has been translated.',
  PartiallyTranslated = 'It looks like your translation is still in progress',
  SuccessfullyAuthored = 'Successfully authored to Contentful!',
  PartiallyAuthored = 'Some of your translations were not successfully authored. Please review your translations.',
}

export enum FailureTypes {
  NoAssociatedGroup = 'Oops, something went wrong! Have you submitted the translation request for the page? There is no associated translation group with the page.',
  OtherFailure = 'Oops, something went wrong! Please try again.',
  TimedOut = 'Sorry, your request timed out! It may have been too large or contained a problem key.'
}

export type LocaleSelections = Record<string, Record<string, boolean>>;

const isAuthorableLocale = (locale: string, value: any) => (
  Boolean(value) && !['en', 'en-us'].includes(locale.toLowerCase())
);

export const createLocaleSelections = (translations: any): LocaleSelections => (
  Object.keys(translations || {}).reduce((selections: LocaleSelections, key: string) => ({
    ...selections,
    [key]: Object.keys(translations[key]?.languages || {}).reduce((locales, locale) => ({
      ...locales,
      [locale]: isAuthorableLocale(locale, translations[key].languages[locale]),
    }), {} as Record<string, boolean>),
  }), {} as LocaleSelections)
);

export const filterTranslationsForAuthoring = (
  translations: any,
  selectedLocales: LocaleSelections,
) => Object.keys(translations || {}).reduce((filteredTranslations, key) => {
  const translation = translations[key];
  const languages = Object.keys(translation?.languages || {}).reduce((selectedLanguages, locale) => {
    const value = translation.languages[locale];
    if (selectedLocales[key]?.[locale] && isAuthorableLocale(locale, value)) {
      return { ...selectedLanguages, [locale]: value };
    }
    return selectedLanguages;
  }, {} as Record<string, any>);

  if (!Object.keys(languages).length) {
    return filteredTranslations;
  }

  return {
    ...filteredTranslations,
    [key]: {
      ...translation,
      languages,
    },
  };
}, {} as Record<string, any>);

const getSafeValidationErrors = (validationErrors: any) => {
  if (!Array.isArray(validationErrors)) {
    return [];
  }

  return validationErrors.reduce((safeErrors, validationError) => {
    const path = validationError?.path;
    const rule = validationError?.rule;
    const message = validationError?.message;
    const safePath = Array.isArray(path) && path.length && path.every((item) => (
      (typeof item === 'string' && item.length)
      || (Number.isSafeInteger(item) && item >= 0)
    )) ? path : undefined;
    const safeRule = typeof rule === 'string' && rule.length ? rule : undefined;
    const safeMessage = typeof message === 'string' && message.length ? message : undefined;

    if (!safePath && !safeRule && !safeMessage) {
      return safeErrors;
    }

    return [...safeErrors, {
      ...(safePath ? { path: safePath } : {}),
      ...(safeRule ? { rule: safeRule } : {}),
      ...(safeMessage ? { message: safeMessage } : {}),
    }];
  }, [] as any[]);
};

export const formatAuthoringDiagnostics = (response: any) => {
  const entries = Array.isArray(response?.data) ? response.data : [response];
  return entries.reduce((diagnostics: string[], entry: any) => {
    const entryPrefix = entry?.id ? `Entry ${entry.id}` : 'Translation save';
    const validationErrors = getSafeValidationErrors(entry?.validationErrors);

    if (validationErrors.length) {
      return [
        ...diagnostics,
        ...validationErrors.map((validationError: any) => {
          const path = validationError.path || [];
          const field = path[0] === 'fields' ? path[1] : undefined;
          const locale = path[0] === 'fields' ? path[2] : undefined;
          const location = [field, locale].filter(Boolean).join(' · ');
          const rule = validationError.rule ? `${validationError.rule}: ` : '';
          return `${entryPrefix}${location ? ` · ${location}` : ''} — ${rule}${validationError.message || 'Validation error'}`;
        }),
      ];
    }

    const summary = [entry?.status, entry?.name, entry?.message].filter(Boolean).join(' · ');
    return [...diagnostics, `${entryPrefix}${summary ? ` — ${summary}` : ' — Validation error'}`];
  }, []);
};

/**
 * Fetches translations of an entry
 * @param translationServiceUrl
 * @param entry
 * @param namespace
 * @param deliveryAccessToken
 * @param previewAccessToken
 * @param space
 * @param environment
 * @returns
 */
export const fetchTranslations = async (
  translationServiceUrl: string,
  entry: string,
  namespace: string,
  deliveryAccessToken: string,
  previewAccessToken: string,
  space: string,
  environment: string,
) => {
  const res = await fetch(`${translationServiceUrl}/page/${entry}/translations/${namespace}?valueType=fieldType&apiType=preview&environment=${environment}`, {
    headers: {
      'X-Contentful-Delivery-Access-Token': deliveryAccessToken,
      'X-Contentful-Preview-Access-Token': previewAccessToken,
      'X-Contentful-Space': space,
    }
  });

  const resJson = await res.json();

  if (resJson.status >= 400) {
    return Promise.reject(resJson);
  }
  return resJson;
};

/**
 * Fetches translations from contentful-all namespace first and then from contentful namespace
 * @param translationServiceUrl
 * @param entry
 * @param sdkSpace
 * @param translationNamespaceMap
 * @param deliveryAccessToken
 * @param previewAccessToken
 * @param space
 * @param environment
 * @returns
 */
export const getTranslations = async (
  translationServiceUrl: string,
  entry: string,
  sdkSpace: any,
  translationNamespaceMap: any,
  deliveryAccessToken: string,
  previewAccessToken: string,
  space: string,
  environment: string,
) => {
  // use sdk to get the most recent tags
  const {
    metadata: {
      tags = [],
    } = {}
  } = await sdkSpace.getEntry(entry);

  const namespaces = tags?.map((tag: any) => tag?.sys?.id)
    .filter((tag: any) => tag.startsWith(TRANSLATION_TAG_PREFIX))
    .map((tag: any) => tag.replace(TRANSLATION_TAG_PREFIX, ''));

  const orderedNamespaces = translationNamespaceMap.reduce((acc: any, cur: any) => {
    if (namespaces.includes(cur.namespace)) {
      return [...acc, cur.namespace]
    }
    return acc;
  }, []);

  if (!orderedNamespaces.length) {
    // if there is no namespace tag associated with the entry
    // assume that no trnaslation request has been made
    // add a tag manually if this has to be bypassed
    return Promise.reject({ status: 404 });
  }

  // fetch translation data from each name space
  for (let index = 0; index < orderedNamespaces.length; index++) {
    try {
      const contentfulNameSpaceData = await fetchTranslations(
        translationServiceUrl,
        entry,
        orderedNamespaces[index],
        deliveryAccessToken,
        previewAccessToken,
        space,
        environment,
      );

      // If we get translations for contentful-all namespace then use them as translations.
      return {
        namespace: orderedNamespaces[index],
        translations: contentfulNameSpaceData?.data?.translations,
      };
    } catch (e: any) {
      // 404 - group hasn't been created
      // 422 - group has been created but there is no associated tags
      if (e.status !== 404 && e.status !== 422) {
        return Promise.reject(e);
      }
      // if 404 or 422, keep going unless it's the last one
      if (index === orderedNamespaces.length - 1) {
        return Promise.reject(e);
      }
    }
  }
};

/**
 * Fetches types of fields of an entry
 * @param translationServiceUrl
 * @param entry
 * @param deliveryAccessToken
 * @param previewAccessToken
 * @param space
 * @param environment
 * @returns
 */
export const fetchLocalizedFieldType = async (
  translationServiceUrl: string,
  entry: string,
  deliveryAccessToken: string,
  previewAccessToken: string,
  space: string,
  environment: string,
) => {
  // Using preview api type since a user may have submitted translations with draft status
  // A field type will not change regardless of apiType
  const res = await fetch(`${translationServiceUrl}/page/${entry}/keys?valueType=fieldType&apiType=preview&environment=${environment}`, {
    headers: {
      'X-Contentful-Delivery-Access-Token': deliveryAccessToken,
      'X-Contentful-Preview-Access-Token': previewAccessToken,
      'X-Contentful-Space': space,
    }
  })
  const resJson = await res.json();

  if (resJson && resJson.status >= 400) {
    return Promise.reject(resJson);
  }

  return resJson;
};

export const checkTranslations = async (
  translationServiceUrl: string,
  entry: string,
  deliveryAccessToken: string,
  previewAccessToken: string,
  space: string,
  environment: string,
  sdkSpace: any,
  translationNamespaceMap: any,
) => {
  try {
    const [{ namespace, translations }, localizedFieldsData]: [any, any] = await Promise.all([
      getTranslations(
        translationServiceUrl,
        entry,
        sdkSpace,
        translationNamespaceMap,
        deliveryAccessToken,
        previewAccessToken,
        space,
        environment,
      ),
      fetchLocalizedFieldType(
        translationServiceUrl,
        entry,
        deliveryAccessToken,
        previewAccessToken,
        space,
        environment,
      ),
    ]);

    // now filter the translations so that only the keys relevant to
    // the latest content of the page are taken into account
    const localizedFieldsKeys = localizedFieldsData?.data ?? {};

    // compute the result
    let numberOfCompletedKeys = 0;
    let totalNumberOfKeys = 0; // keys that are relevant to the latest status of the page. NOT all the keys from the first submission

    type Translation = {
      status: {
        total: number;
        translated: number;
      };
      languages: Record<string, string>;
    };

    const filteredTranslations = Object.keys(translations).reduce((acc, key) => {
      if (!localizedFieldsKeys[key]) {
        return acc;
      }

      totalNumberOfKeys++;

      const total = translations?.[key]?.status.total;
      const translated = translations?.[key]?.status.translated;
      if (total === translated) {
        numberOfCompletedKeys++;
      }

      return {
        ...acc,
        [key]: translations[key],
      };
    }, {} as Record<string, Translation>);

    const filteredTranslationData = {
      data: {
        status: {
          total: totalNumberOfKeys,
          translated: numberOfCompletedKeys,
        },
        translations: filteredTranslations,
      }
    };

    if (totalNumberOfKeys === numberOfCompletedKeys) {
      return {
        status: Status.Success,
        namespace,
        type: SuccessTypes.AllTranslated,
        translations: filteredTranslationData,
        localizedFields: localizedFieldsData,
      };
    } else {
      // partially completed
      return {
        status: Status.Success,
        namespace,
        type: SuccessTypes.PartiallyTranslated,
        translations: filteredTranslationData,
        localizedFields: localizedFieldsData,
      };
    }
  } catch (error:any) {
    if (error.status === 404) {
      return Promise.reject({
        status: Status.Failure,
        type: FailureTypes.NoAssociatedGroup,
        error,
      });
    }

    return Promise.reject({
      status: Status.Failure,
      type: FailureTypes.OtherFailure,
      error,
    });
  }
};

export const authorTranslations = async ({
  translationServiceUrl,
  autoPublish,
  translations,
  managementAccessToken,
  space,
  testingMode = false,
}: {
  translationServiceUrl: string,
  autoPublish: boolean,
  translations: any,
  managementAccessToken: string,
  space: string,
  testingMode: boolean,
}) => {

  let requests: any
  // break request into batches if there are more than 5
  if (Object.keys(translations).length > 5) {
    const batchRequests = Object.keys(translations).reduce((acc, cur, index) => {
      const batch = Math.floor(index/BATCH_SIZE)
      if (!acc[batch]) acc[batch] = {};

      acc[batch][cur] = translations[cur];

      return acc
    }, [] as any)

    // stringify batches
    requests = batchRequests.map((batch: any) => {
     return JSON.stringify({
            data: {
              "translations": batch,
            }
          })
    })
  } else {
  // if request has less than 5 keys, only stringify
    requests = [JSON.stringify({
      data: {
        translations,
      }
    })];
  }

  let res: any;
  let resJson: any;
  if (!testingMode) {
   // throttle batched requests to 2 per second
   const throttle = throttledQueue(THROTTLE_AMOUNT, THROTTLE_TIME, true);
   try {
      res = await Promise.all(
        requests.map(
          (body:any) => throttle(async () => {
              const data = await fetch(`${translationServiceUrl}/page/translations${ autoPublish ? '?forcePublish=true' : ''}`, {
                method: 'PUT',
                headers: {
                  'X-Contentful-Access-Token': managementAccessToken,
                  'X-Contentful-Space': space,
                  'Content-Type': 'application/json',
                },
                body,
              }).catch((error:any)=> {
                // failed to fetch : timed out
                // eslint-disable-next-line no-throw-literal
                throw { status: 504, message: error }
              })
              return await data.json()
          })
      ))

      // reduce array of responses into one
      resJson = res.reduce((acc:any, cur:any) => {
        const data = Array.isArray(cur?.data) ? cur.data : [{
          status: cur?.status,
          ...(cur?.name ? { name: cur.name } : {}),
          ...(cur?.message ? { message: cur.message } : {}),
        }];
        return {
          status: acc.status === 200 || cur?.status === 200 ? 200 : cur?.status,
          data: [...acc.data, ...data],
        };
      }, { status: 500, data: [] })
    } catch (error : any) {
      if (error.status && error.message) {
         resJson = {
          status: error.status,
          message: error.message
        }
      }
      else {
        resJson = {
          status: 400,
          message: 'Unknown Error'
        }
      }
      return Promise.reject(resJson);
    }
  }

   else {
    resJson = { status:200 }
  }

  if (resJson && resJson.status >= 400) {
    return Promise.reject(resJson);
  }

  return resJson;
};
